// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import { GlobalSettings, SizeAndUnit } from "./shared";
import * as Utils from "./utils";
import * as Enums from "./enums";
import { Strings } from "./strings";
export class Version {
    constructor(major = 1, minor = 1, label) {
        this._isValid = true;
        this._major = major;
        this._minor = minor;
        this._label = label;
    }
    static parse(versionString, context) {
        if (!versionString) {
            return undefined;
        }
        const result = new Version();
        result._versionString = versionString;
        const regEx = /(\d+).(\d+)/gi;
        const matches = regEx.exec(versionString);
        if (matches != null && matches.length === 3) {
            result._major = parseInt(matches[1]);
            result._minor = parseInt(matches[2]);
        }
        else {
            result._isValid = false;
        }
        if (!result._isValid) {
            context.logParseEvent(undefined, Enums.ValidationEvent.InvalidPropertyValue, Strings.errors.invalidVersionString(result._versionString));
        }
        return result;
    }
    toString() {
        return !this._isValid ? this._versionString : this._major + "." + this._minor;
    }
    toJSON() {
        return this.toString();
    }
    compareTo(otherVersion) {
        if (!this.isValid || !otherVersion.isValid) {
            throw new Error("Cannot compare invalid version.");
        }
        if (this.major > otherVersion.major) {
            return 1;
        }
        else if (this.major < otherVersion.major) {
            return -1;
        }
        else if (this.minor > otherVersion.minor) {
            return 1;
        }
        else if (this.minor < otherVersion.minor) {
            return -1;
        }
        return 0;
    }
    get label() {
        return this._label ? this._label : this.toString();
    }
    get major() {
        return this._major;
    }
    get minor() {
        return this._minor;
    }
    get isValid() {
        return this._isValid;
    }
}
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class Versions {
    // If preview tag is added/removed from any version,
    // don't forget to update .ac-schema-version-1-?::after too in adaptivecards-site\themes\adaptivecards\source\css\style.css
    /* eslint-enable @typescript-eslint/naming-convention */
    static getAllDeclaredVersions() {
        const ctor = Versions;
        const properties = [];
        for (const propertyName in ctor) {
            if (propertyName.match(/^v[0-9_]*$/)) {
                // filter latest
                try {
                    const propertyValue = ctor[propertyName];
                    if (propertyValue instanceof Version) {
                        properties.push(propertyValue);
                    }
                }
                catch (_a) {
                    // If a property happens to have a getter function and
                    // it throws an exception, we need to catch it here
                }
            }
        }
        return properties.sort((v1, v2) => v1.compareTo(v2));
    }
}
/* eslint-disable @typescript-eslint/naming-convention */
Versions.v1_0 = new Version(1, 0);
Versions.v1_1 = new Version(1, 1);
Versions.v1_2 = new Version(1, 2);
Versions.v1_3 = new Version(1, 3);
Versions.v1_4 = new Version(1, 4);
Versions.v1_5 = new Version(1, 5);
Versions.v1_6 = new Version(1, 6);
Versions.latest = Versions.v1_6;
export function isVersionLessOrEqual(version, targetVersion) {
    if (version instanceof Version) {
        if (targetVersion instanceof Version) {
            return targetVersion.compareTo(version) >= 0;
        }
        else {
            // Target version is *
            return true;
        }
    }
    else {
        // Version is *
        return true;
    }
}
export class BaseSerializationContext {
    constructor(targetVersion = Versions.latest) {
        this._validationEvents = [];
        this.targetVersion = targetVersion;
    }
    isTemplateString(value) {
        if (typeof value === "string") {
            return value.startsWith("${");
        }
        else {
            return false;
        }
    }
    tryDeleteValue(target, propertyName) {
        if (!GlobalSettings.enableFullJsonRoundTrip) {
            delete target[propertyName];
        }
    }
    tryDeleteDefaultValue(target, propertyName) {
        if (!GlobalSettings.enableFullJsonRoundTrip || !this.isTemplateString(target[propertyName])) {
            delete target[propertyName];
        }
    }
    serializeValue(target, propertyName, propertyValue, defaultValue = undefined, forceDeleteIfNullOrDefault = false) {
        if (propertyValue === null ||
            propertyValue === undefined) {
            if (!GlobalSettings.enableFullJsonRoundTrip || forceDeleteIfNullOrDefault) {
                delete target[propertyName];
            }
        }
        else if (propertyValue === defaultValue) {
            if (!GlobalSettings.enableFullJsonRoundTrip || forceDeleteIfNullOrDefault || !this.isTemplateString(target[propertyName])) {
                delete target[propertyName];
            }
        }
        else {
            target[propertyName] = propertyValue;
        }
    }
    serializeString(target, propertyName, propertyValue, defaultValue) {
        if (propertyValue === null ||
            propertyValue === undefined) {
            this.tryDeleteValue(target, propertyName);
        }
        else if (propertyValue === defaultValue) {
            this.tryDeleteDefaultValue(target, propertyName);
        }
        else {
            target[propertyName] = propertyValue;
        }
    }
    serializeBool(target, propertyName, propertyValue, defaultValue) {
        if (propertyValue === null ||
            propertyValue === undefined) {
            this.tryDeleteValue(target, propertyName);
        }
        else if (propertyValue === defaultValue) {
            this.tryDeleteDefaultValue(target, propertyName);
        }
        else {
            target[propertyName] = propertyValue;
        }
    }
    serializeNumber(target, propertyName, propertyValue, defaultValue) {
        if (propertyValue === null ||
            propertyValue === undefined ||
            isNaN(propertyValue)) {
            this.tryDeleteValue(target, propertyName);
        }
        else if (propertyValue === defaultValue) {
            this.tryDeleteDefaultValue(target, propertyName);
        }
        else {
            target[propertyName] = propertyValue;
        }
    }
    serializeEnum(enumType, target, propertyName, propertyValue, defaultValue = undefined) {
        if (propertyValue === null ||
            propertyValue === undefined) {
            this.tryDeleteValue(target, propertyName);
        }
        else if (propertyValue === defaultValue) {
            this.tryDeleteDefaultValue(target, propertyName);
        }
        else {
            target[propertyName] = enumType[propertyValue];
        }
    }
    serializeArray(target, propertyName, propertyValue) {
        const items = [];
        if (propertyValue) {
            for (const item of propertyValue) {
                let serializedItem = undefined;
                if (item instanceof SerializableObject) {
                    serializedItem = item.toJSON(this);
                }
                else if (item.toJSON) {
                    serializedItem = item.toJSON();
                }
                else {
                    serializedItem = item;
                }
                if (serializedItem !== undefined) {
                    items.push(serializedItem);
                }
            }
        }
        if (items.length === 0) {
            if (target.hasOwnProperty(propertyName) && Array.isArray(target[propertyName])) {
                delete target[propertyName];
            }
        }
        else {
            this.serializeValue(target, propertyName, items);
        }
    }
    clearEvents() {
        this._validationEvents = [];
    }
    logEvent(source, phase, event, message) {
        this._validationEvents.push({
            source: source,
            phase: phase,
            event: event,
            message: message
        });
    }
    logParseEvent(source, event, message) {
        this.logEvent(source, Enums.ValidationPhase.Parse, event, message);
    }
    getEventAt(index) {
        return this._validationEvents[index];
    }
    get eventCount() {
        return this._validationEvents.length;
    }
}
class SimpleSerializationContext extends BaseSerializationContext {
}
export class PropertyDefinition {
    getInternalName() {
        return this.name;
    }
    parse(sender, source, context) {
        return source[this.name];
    }
    toJSON(sender, target, value, context) {
        context.serializeValue(target, this.name, value, this.defaultValue);
    }
    constructor(targetVersion, name, defaultValue, onGetInitialValue) {
        this.targetVersion = targetVersion;
        this.name = name;
        this.defaultValue = defaultValue;
        this.onGetInitialValue = onGetInitialValue;
        this.isSerializationEnabled = true;
        this.sequentialNumber = PropertyDefinition._sequentialNumber;
        PropertyDefinition._sequentialNumber++;
    }
}
PropertyDefinition._sequentialNumber = 0;
export class StringProperty extends PropertyDefinition {
    parse(sender, source, context) {
        const parsedValue = Utils.parseString(source[this.name], this.defaultValue);
        const isUndefined = parsedValue === undefined || (parsedValue === "" && this.treatEmptyAsUndefined);
        if (!isUndefined && this.regEx !== undefined) {
            const matches = this.regEx.exec(parsedValue);
            if (!matches) {
                context.logParseEvent(sender, Enums.ValidationEvent.InvalidPropertyValue, Strings.errors.invalidPropertyValue(parsedValue, this.name));
                return undefined;
            }
        }
        return parsedValue;
    }
    toJSON(sender, target, value, context) {
        context.serializeString(target, this.name, value === "" && this.treatEmptyAsUndefined ? undefined : value, this.defaultValue);
    }
    constructor(targetVersion, name, treatEmptyAsUndefined = true, regEx, defaultValue, onGetInitialValue) {
        super(targetVersion, name, defaultValue, onGetInitialValue);
        this.targetVersion = targetVersion;
        this.name = name;
        this.treatEmptyAsUndefined = treatEmptyAsUndefined;
        this.regEx = regEx;
        this.defaultValue = defaultValue;
        this.onGetInitialValue = onGetInitialValue;
    }
}
export class BoolProperty extends PropertyDefinition {
    parse(sender, source, context) {
        return Utils.parseBool(source[this.name], this.defaultValue);
    }
    toJSON(sender, target, value, context) {
        context.serializeBool(target, this.name, value, this.defaultValue);
    }
    constructor(targetVersion, name, defaultValue, onGetInitialValue) {
        super(targetVersion, name, defaultValue, onGetInitialValue);
        this.targetVersion = targetVersion;
        this.name = name;
        this.defaultValue = defaultValue;
        this.onGetInitialValue = onGetInitialValue;
    }
}
export class NumProperty extends PropertyDefinition {
    parse(sender, source, context) {
        return Utils.parseNumber(source[this.name], this.defaultValue);
    }
    toJSON(sender, target, value, context) {
        context.serializeNumber(target, this.name, value, this.defaultValue);
    }
    constructor(targetVersion, name, defaultValue, onGetInitialValue) {
        super(targetVersion, name, defaultValue, onGetInitialValue);
        this.targetVersion = targetVersion;
        this.name = name;
        this.defaultValue = defaultValue;
        this.onGetInitialValue = onGetInitialValue;
    }
}
export class PixelSizeProperty extends PropertyDefinition {
    parse(sender, source, context) {
        let result = undefined;
        const value = source[this.name];
        if (typeof value === "string") {
            let isValid = false;
            try {
                const size = SizeAndUnit.parse(value, true);
                if (size.unit === Enums.SizeUnit.Pixel) {
                    result = size.physicalSize;
                    isValid = true;
                }
            }
            catch (_a) {
                // Do nothing. A parse error is emitted below
            }
            if (!isValid) {
                context.logParseEvent(sender, Enums.ValidationEvent.InvalidPropertyValue, Strings.errors.invalidPropertyValue(source[this.name], this.name));
            }
        }
        return result;
    }
    toJSON(sender, target, value, context) {
        context.serializeValue(target, this.name, typeof value === "number" && !isNaN(value) ? value + "px" : undefined);
    }
}
export class StringArrayProperty extends PropertyDefinition {
    parse(sender, source, context) {
        const sourceValue = source[this.name];
        if (sourceValue === undefined || !Array.isArray(sourceValue)) {
            return this.defaultValue;
        }
        const result = [];
        for (const value of sourceValue) {
            if (typeof value === "string") {
                result.push(value);
            }
            else {
                context.logParseEvent(sender, Enums.ValidationEvent.InvalidPropertyValue, `Invalid array value "${JSON.stringify(value)}" of type "${typeof value}" ignored for "${this.name}".`);
            }
        }
        return result;
    }
    toJSON(sender, target, value, context) {
        context.serializeArray(target, this.name, value);
    }
    constructor(targetVersion, name, defaultValue, onGetInitialValue) {
        super(targetVersion, name, defaultValue, onGetInitialValue);
        this.targetVersion = targetVersion;
        this.name = name;
        this.defaultValue = defaultValue;
        this.onGetInitialValue = onGetInitialValue;
    }
}
export class ValueSetProperty extends PropertyDefinition {
    isValidValue(value, context) {
        for (const versionedValue of this.values) {
            if (value.toLowerCase() === versionedValue.value.toLowerCase()) {
                const targetVersion = versionedValue.targetVersion
                    ? versionedValue.targetVersion
                    : this.targetVersion;
                return targetVersion.compareTo(context.targetVersion) <= 0;
            }
        }
        return false;
    }
    parse(sender, source, context) {
        const sourceValue = source[this.name];
        if (sourceValue === undefined) {
            return this.defaultValue;
        }
        if (typeof sourceValue === "string") {
            for (const versionedValue of this.values) {
                if (sourceValue.toLowerCase() === versionedValue.value.toLowerCase()) {
                    const targetVersion = versionedValue.targetVersion
                        ? versionedValue.targetVersion
                        : this.targetVersion;
                    if (targetVersion.compareTo(context.targetVersion) <= 0) {
                        return versionedValue.value;
                    }
                    else {
                        context.logParseEvent(sender, Enums.ValidationEvent.InvalidPropertyValue, Strings.errors.propertyValueNotSupported(sourceValue, this.name, targetVersion.toString(), context.targetVersion.toString()));
                        return this.defaultValue;
                    }
                }
            }
        }
        context.logParseEvent(sender, Enums.ValidationEvent.InvalidPropertyValue, Strings.errors.invalidPropertyValue(sourceValue, this.name));
        return this.defaultValue;
    }
    toJSON(sender, target, value, context) {
        let invalidValue = false;
        if (value !== undefined) {
            invalidValue = true;
            for (const versionedValue of this.values) {
                if (versionedValue.value === value) {
                    const targetVersion = versionedValue.targetVersion
                        ? versionedValue.targetVersion
                        : this.targetVersion;
                    if (targetVersion.compareTo(context.targetVersion) <= 0) {
                        invalidValue = false;
                        break;
                    }
                    else {
                        context.logEvent(sender, Enums.ValidationPhase.ToJSON, Enums.ValidationEvent.InvalidPropertyValue, Strings.errors.propertyValueNotSupported(value, this.name, targetVersion.toString(), context.targetVersion.toString()));
                    }
                }
            }
        }
        if (!invalidValue) {
            context.serializeValue(target, this.name, value, this.defaultValue, true);
        }
    }
    constructor(targetVersion, name, values, defaultValue, onGetInitialValue) {
        super(targetVersion, name, defaultValue, onGetInitialValue);
        this.targetVersion = targetVersion;
        this.name = name;
        this.values = values;
        this.defaultValue = defaultValue;
        this.onGetInitialValue = onGetInitialValue;
    }
}
export class EnumProperty extends PropertyDefinition {
    parse(sender, source, context) {
        const sourceValue = source[this.name];
        if (typeof sourceValue !== "string") {
            return this.defaultValue;
        }
        const enumValue = Utils.getEnumValueByName(this.enumType, sourceValue);
        if (enumValue !== undefined) {
            for (const versionedValue of this.values) {
                if (versionedValue.value === enumValue) {
                    const targetVersion = versionedValue.targetVersion
                        ? versionedValue.targetVersion
                        : this.targetVersion;
                    if (targetVersion.compareTo(context.targetVersion) <= 0) {
                        return enumValue;
                    }
                    else {
                        context.logParseEvent(sender, Enums.ValidationEvent.InvalidPropertyValue, Strings.errors.propertyValueNotSupported(sourceValue, this.name, targetVersion.toString(), context.targetVersion.toString()));
                        return this.defaultValue;
                    }
                }
            }
        }
        context.logParseEvent(sender, Enums.ValidationEvent.InvalidPropertyValue, Strings.errors.invalidPropertyValue(sourceValue, this.name));
        return this.defaultValue;
    }
    toJSON(sender, target, value, context) {
        let invalidValue = false;
        if (value !== undefined) {
            invalidValue = true;
            for (const versionedValue of this.values) {
                if (versionedValue.value === value) {
                    const targetVersion = versionedValue.targetVersion
                        ? versionedValue.targetVersion
                        : this.targetVersion;
                    if (targetVersion.compareTo(context.targetVersion) <= 0) {
                        invalidValue = false;
                        break;
                    }
                    else {
                        context.logEvent(sender, Enums.ValidationPhase.ToJSON, Enums.ValidationEvent.InvalidPropertyValue, Strings.errors.invalidPropertyValue(value, this.name));
                    }
                }
            }
        }
        if (!invalidValue) {
            context.serializeEnum(this.enumType, target, this.name, value, this.defaultValue);
        }
    }
    constructor(targetVersion, name, enumType, defaultValue, values, onGetInitialValue) {
        super(targetVersion, name, defaultValue, onGetInitialValue);
        this.targetVersion = targetVersion;
        this.name = name;
        this.enumType = enumType;
        this.defaultValue = defaultValue;
        this.onGetInitialValue = onGetInitialValue;
        this._values = [];
        if (!values) {
            // eslint-disable-next-line guard-for-in
            for (const key in enumType) {
                const keyAsNumber = parseInt(key, 10);
                if (keyAsNumber >= 0) {
                    this._values.push({ value: keyAsNumber });
                }
            }
        }
        else {
            this._values = values;
        }
    }
    get values() {
        return this._values;
    }
}
export class SerializableObjectProperty extends PropertyDefinition {
    parse(sender, source, context) {
        const sourceValue = source[this.name];
        if (sourceValue === undefined) {
            return this.onGetInitialValue ? this.onGetInitialValue(sender) : this.defaultValue;
        }
        const result = new this.objectType();
        result.parse(sourceValue, context);
        return result;
    }
    toJSON(sender, target, value, context) {
        let serializedValue = undefined;
        if (value !== undefined && !value.hasAllDefaultValues()) {
            serializedValue = value.toJSON(context);
        }
        if (typeof serializedValue === "object" && Object.keys(serializedValue).length === 0) {
            serializedValue = undefined;
        }
        context.serializeValue(target, this.name, serializedValue, this.defaultValue, true);
    }
    constructor(targetVersion, name, objectType, nullable = false, defaultValue) {
        super(targetVersion, name, defaultValue, (sender) => {
            return this.nullable ? undefined : new this.objectType();
        });
        this.targetVersion = targetVersion;
        this.name = name;
        this.objectType = objectType;
        this.nullable = nullable;
    }
}
export class SerializableObjectCollectionProperty extends PropertyDefinition {
    parse(sender, source, context) {
        const result = [];
        const sourceCollection = source[this.name];
        if (Array.isArray(sourceCollection)) {
            for (const sourceItem of sourceCollection) {
                const item = new this.objectType();
                item.parse(sourceItem, context);
                result.push(item);
                if (this.onItemAdded) {
                    this.onItemAdded(sender, item);
                }
            }
        }
        return result.length > 0
            ? result
            : this.onGetInitialValue
                ? this.onGetInitialValue(sender)
                : undefined;
    }
    toJSON(sender, target, value, context) {
        context.serializeArray(target, this.name, value);
    }
    constructor(targetVersion, name, objectType, onItemAdded) {
        super(targetVersion, name, undefined, (sender) => {
            return [];
        });
        this.targetVersion = targetVersion;
        this.name = name;
        this.objectType = objectType;
        this.onItemAdded = onItemAdded;
    }
}
export class CustomProperty extends PropertyDefinition {
    parse(sender, source, context) {
        return this.onParse(sender, this, source, context);
    }
    toJSON(sender, target, value, context) {
        this.onToJSON(sender, this, target, value, context);
    }
    constructor(targetVersion, name, onParse, onToJSON, defaultValue, onGetInitialValue) {
        super(targetVersion, name, defaultValue, onGetInitialValue);
        this.targetVersion = targetVersion;
        this.name = name;
        this.onParse = onParse;
        this.onToJSON = onToJSON;
        this.defaultValue = defaultValue;
        this.onGetInitialValue = onGetInitialValue;
        if (!this.onParse) {
            throw new Error("CustomPropertyDefinition instances must have an onParse handler.");
        }
        if (!this.onToJSON) {
            throw new Error("CustomPropertyDefinition instances must have an onToJSON handler.");
        }
    }
}
export class SerializableObjectSchema {
    constructor() {
        this._properties = [];
    }
    indexOf(prop) {
        for (let i = 0; i < this._properties.length; i++) {
            if (this._properties[i] === prop) {
                return i;
            }
        }
        return -1;
    }
    add(...properties) {
        for (const prop of properties) {
            if (this.indexOf(prop) === -1) {
                this._properties.push(prop);
            }
        }
    }
    remove(...properties) {
        for (const prop of properties) {
            while (true) {
                const index = this.indexOf(prop);
                if (index >= 0) {
                    this._properties.splice(index, 1);
                }
                else {
                    break;
                }
            }
        }
    }
    getItemAt(index) {
        return this._properties[index];
    }
    getCount() {
        return this._properties.length;
    }
}
// This is a decorator function, used to map SerializableObject descendant class members to
// schema properties
export function property(prop) {
    return function (target, propertyKey) {
        const descriptor = Object.getOwnPropertyDescriptor(target, propertyKey) || {};
        if (!descriptor.get && !descriptor.set) {
            descriptor.get = function () {
                return this.getValue(prop);
            };
            descriptor.set = function (value) {
                this.setValue(prop, value);
            };
            Object.defineProperty(target, propertyKey, descriptor);
        }
    };
}
export class SerializableObject {
    getDefaultSerializationContext() {
        return new SimpleSerializationContext();
    }
    populateSchema(schema) {
        const ctor = this.constructor;
        const properties = [];
        // eslint-disable-next-line guard-for-in
        for (const propertyName in ctor) {
            try {
                const propertyValue = ctor[propertyName];
                if (propertyValue instanceof PropertyDefinition) {
                    properties.push(propertyValue);
                }
            }
            catch (_a) {
                // If a property happens to have a getter function and
                // it throws an exception, we need to catch it here
            }
        }
        if (properties.length > 0) {
            const sortedProperties = properties.sort((p1, p2) => {
                if (p1.sequentialNumber > p2.sequentialNumber) {
                    return 1;
                }
                else if (p1.sequentialNumber < p2.sequentialNumber) {
                    return -1;
                }
                return 0;
            });
            schema.add(...sortedProperties);
        }
        if (SerializableObject.onRegisterCustomProperties) {
            SerializableObject.onRegisterCustomProperties(this, schema);
        }
    }
    getValue(prop) {
        return this._propertyBag.hasOwnProperty(prop.getInternalName())
            ? this._propertyBag[prop.getInternalName()]
            : prop.defaultValue;
    }
    setValue(prop, value) {
        if (value === undefined || value === null) {
            delete this._propertyBag[prop.getInternalName()];
        }
        else {
            this._propertyBag[prop.getInternalName()] = value;
        }
    }
    internalParse(source, context) {
        this._propertyBag = {};
        this._rawProperties = GlobalSettings.enableFullJsonRoundTrip ? (source ? source : {}) : {};
        if (source) {
            const s = this.getSchema();
            for (let i = 0; i < s.getCount(); i++) {
                const prop = s.getItemAt(i);
                if (prop.isSerializationEnabled) {
                    let propertyValue = prop.onGetInitialValue
                        ? prop.onGetInitialValue(this)
                        : undefined;
                    if (source.hasOwnProperty(prop.name)) {
                        if (prop.targetVersion.compareTo(context.targetVersion) <= 0) {
                            propertyValue = prop.parse(this, source, context);
                        }
                        else {
                            context.logParseEvent(this, Enums.ValidationEvent.UnsupportedProperty, Strings.errors.propertyNotSupported(prop.name, prop.targetVersion.toString(), context.targetVersion.toString()));
                        }
                    }
                    this.setValue(prop, propertyValue);
                }
            }
        }
        else {
            this.resetDefaultValues();
        }
    }
    internalToJSON(target, context) {
        const s = this.getSchema();
        const serializedProperties = [];
        for (let i = 0; i < s.getCount(); i++) {
            const prop = s.getItemAt(i);
            // Avoid serializing the same property multiple times. This is necessary
            // because some property definitions map to the same underlying schema
            // property
            if (prop.isSerializationEnabled &&
                prop.targetVersion.compareTo(context.targetVersion) <= 0 &&
                serializedProperties.indexOf(prop.name) === -1) {
                prop.toJSON(this, target, this.getValue(prop), context);
                serializedProperties.push(prop.name);
            }
        }
    }
    shouldSerialize(_context) {
        return true;
    }
    constructor() {
        this._propertyBag = {};
        this._rawProperties = {};
        this.maxVersion = SerializableObject.defaultMaxVersion;
        const s = this.getSchema();
        for (let i = 0; i < s.getCount(); i++) {
            const prop = s.getItemAt(i);
            if (prop.onGetInitialValue) {
                this.setValue(prop, prop.onGetInitialValue(this));
            }
        }
    }
    parse(source, context) {
        this.internalParse(source, context ? context : new SimpleSerializationContext());
    }
    toJSON(context) {
        let effectiveContext;
        if (context && context instanceof BaseSerializationContext) {
            effectiveContext = context;
        }
        else {
            effectiveContext = this.getDefaultSerializationContext();
            effectiveContext.toJSONOriginalParam = context;
        }
        if (this.shouldSerialize(effectiveContext)) {
            let result;
            if (GlobalSettings.enableFullJsonRoundTrip &&
                this._rawProperties &&
                typeof this._rawProperties === "object") {
                result = this._rawProperties;
            }
            else {
                result = {};
            }
            this.internalToJSON(result, effectiveContext);
            return result;
        }
        else {
            return undefined;
        }
    }
    hasDefaultValue(prop) {
        return this.getValue(prop) === prop.defaultValue;
    }
    hasAllDefaultValues() {
        const s = this.getSchema();
        for (let i = 0; i < s.getCount(); i++) {
            const prop = s.getItemAt(i);
            if (!this.hasDefaultValue(prop)) {
                return false;
            }
        }
        return true;
    }
    resetDefaultValues() {
        const s = this.getSchema();
        for (let i = 0; i < s.getCount(); i++) {
            const prop = s.getItemAt(i);
            this.setValue(prop, prop.defaultValue);
        }
    }
    setCustomProperty(name, value) {
        const shouldDeleteProperty = (typeof value === "string" && !value) || value === undefined || value === null;
        if (shouldDeleteProperty) {
            delete this._rawProperties[name];
        }
        else {
            this._rawProperties[name] = value;
        }
    }
    getCustomProperty(name) {
        return this._rawProperties[name];
    }
    getSchema() {
        let schema = SerializableObject._schemaCache[this.getSchemaKey()];
        if (!schema) {
            schema = new SerializableObjectSchema();
            this.populateSchema(schema);
            SerializableObject._schemaCache[this.getSchemaKey()] = schema;
        }
        return schema;
    }
}
SerializableObject.defaultMaxVersion = Versions.latest;
SerializableObject._schemaCache = {};
//# sourceMappingURL=serialization.js.map